import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Autocomplete } from "@mui/material";
import theme from "../../../assets/theme";
import { consumeService, getReportData, saveDocs } from "../../../service/network";
import { useLoading } from "../../../context/loading.context";
import { useNotification } from "../../../context/notification.context";
import moment from "moment/moment";
import AttachmentIcon from '@mui/icons-material/Attachment';
import { uploadFrappeMedia } from "../../../service/network";
import { ChildCareSharp } from "@mui/icons-material";
import { FRAPPE_DIRECT_URL } from "../../../service/network";
import { Chip, Stack } from "@mui/material";
const initialServices = [
  {
    date: "2025-05-30",
    service: "Breakfast",
    description: "Continental breakfast with beverages",
    cost: 1234,
  },
  {
    date: "2025-05-29",
    service: "Extra Bed",
    description: "Single extra bed in the room",
    cost: 1234,
  },
  {
    date: "2025-05-28",
    service: "Laundry",
    description: "Clothes washed and ironed",
    cost: 1234,
  },
];

function AddServiceDialog({ open, onClose, onAdd, showDateField, initialData }) {
  const [formData, setFormData] = useState({
    date: "",
    service: "",
    description: "",
    cost_to_company: "",
    markup_percentage: "",
    cost_to_customer: "",
    attachment: null
  });

  const [services, setServices] = useState([]);
  const [serviceMarkupMap, setServiceMarkupMap] = useState({});
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const resp = await getReportData({
          doctype: "Services",
          fields: ["name", "markup"],
          filters: []
        });

        console.log("Services API Response:", resp);

        let rs = [];
        let markupMap = {};

        if (resp.data && resp.data.message) {
          const data = resp.data.message;

          if (data.values && Array.isArray(data.values)) {
            for (let srv of data.values) {
              if (srv[0]) {
                rs.push(srv[0]);
                markupMap[srv[0]] = srv[1] || 0;
              }
            }
          } else if (Array.isArray(data)) {
            for (let srv of data) {
              if (srv.name) {
                rs.push(srv.name);
                markupMap[srv.name] = srv.markup_percentage || 0;
              }
            }
          }
        }

        setServices(rs);
        setServiceMarkupMap(markupMap);
      } catch (err) {
        console.error("Error fetching services:", err);
        setServices(["Breakfast", "Extra Bed", "Laundry", "Other"]);
      }
    };

    if (open) {
      fetchServices();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        console.log("Initial Data received:", initialData);
        // Use the calculated cost_to_customer directly from initialData
        // Don't recalculate when editing existing service
        setFormData({
          ...initialData
        });
      } else {
        setFormData({
          date: "",
          service: "",
          description: "",
          cost_to_company: "",
          markup_percentage: "",
          cost_to_customer: "",
        });
      }
    }
  }, [open, initialData]);

  // Calculate cost to customer when cost_to_company or markup_percentage changes
  // BUT only for new services, not when editing
  useEffect(() => {
    if (!initialData) { // Only calculate for new services, not when editing
      const costToCompany = parseFloat(formData.cost_to_company);
      const markup = parseFloat(formData.markup_percentage);

      if (!isNaN(costToCompany)) {
        let costToCustomer;

        if (!isNaN(markup)) {
          costToCustomer = costToCompany + (costToCompany * markup / 100);
        } else {
          costToCustomer = costToCompany;
        }

        setFormData(prev => ({
          ...prev,
          cost_to_customer: costToCustomer.toFixed(2)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          cost_to_customer: ""
        }));
      }
    }
  }, [formData.cost_to_company, formData.markup_percentage, initialData]);

  // Auto-fill markup percentage when service is selected
  const handleServiceChange = (serviceName) => {
    const newService = serviceName || "";
    const markup = serviceName ? (serviceMarkupMap[serviceName] || "").toString() : "";

    const updatedFormData = {
      ...formData,
      service: newService,
      markup_percentage: markup
    };

    // Only recalculate for new services
    if (!initialData && formData.cost_to_company && markup) {
      const costToCompany = parseFloat(formData.cost_to_company);
      const markupValue = parseFloat(markup);
      const costToCustomer = costToCompany + (costToCompany * markupValue / 100);
      updatedFormData.cost_to_customer = costToCustomer.toFixed(2);
    } else if (!initialData && formData.cost_to_company && !markup) {
      updatedFormData.cost_to_customer = formData.cost_to_company;
    }

    setFormData(updatedFormData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  }, [open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file || null);
    e.target.value = null;
  };

  const handleSubmit = () => {
    if (!formData.service || !formData.cost_to_company || (showDateField && !formData.date)) {
      console.error("Validation failed:", { formData, showDateField });
      return;
    }

    const submissionData = {
      ...formData,
      cost_to_company: parseFloat(formData.cost_to_company),
      markup_percentage: formData.markup_percentage ? parseFloat(formData.markup_percentage) : 0,
      cost_to_customer: formData.cost_to_customer ? parseFloat(formData.cost_to_customer) : parseFloat(formData.cost_to_company),
      ...(showDateField ? {} : { date: new Date().toISOString().split("T")[0] }),
    };

    console.log("Submitting service data:", submissionData);
    onAdd(submissionData, selectedFile, formData.attachment);;
  };

  const inputBoxStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.borderRadius.medium,
      backgroundColor: theme.palette.lightgray.main,
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      "& fieldset": {
        borderColor: theme.palette.gray.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const autocompletePaperStyle = {
    "& .MuiAutocomplete-option:hover": {
      backgroundColor: "lightred.main",
    },
    borderRadius: theme.borderRadius.medium,
    backgroundColor: "lightred.main",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: 2, backgroundColor: theme.palette.light.main },
      }}
    >
      <DialogTitle variant="SubTitleBold">
        {initialData ? "Edit Availed Service" : "Add Availed Service"}
      </DialogTitle>
      <DialogContent>
        <input
          type="file"
          hidden
          ref={fileInputRef}
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileChange}
        />

        <Grid container spacing={2}>
          {showDateField && (
            <Grid size={12}>
              <Typography variant="bodyText1" sx={{ color: "dark.main", mb: 0.5 }}>
                Date
              </Typography>
              <TextField
                fullWidth
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                sx={inputBoxStyle}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          <Grid size={6}>
            <Typography variant="bodyText1" sx={{ color: "dark.main", mb: 0.5 }}>
              Service
            </Typography>
            <Autocomplete
              freeSolo
              disabled={initialData}
              options={services}
              value={formData.service}
              onChange={(event, value) => handleServiceChange(value)}
              onInputChange={(event, value) => {
                if (!initialData) {
                  setFormData(prev => ({ ...prev, service: value }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type service"
                  sx={inputBoxStyle}
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} sx={autocompletePaperStyle} />
              )}
            />
            {services.length === 0 && (
              <Typography variant="caption" color="error">
                No services found. Please type a service name.
              </Typography>
            )}
          </Grid>

          <Grid size={6}>
            <Typography variant="bodyText1" sx={{ color: "dark.main", mb: 0.5 }}>
              Cost to Company (₹)
            </Typography>
            <TextField
              disabled={initialData}
              fullWidth
              placeholder="Company Cost"
              name="cost_to_company"
              type="number"
              value={formData.cost_to_company}
              onChange={handleChange}
              sx={inputBoxStyle}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
              }}
            />
          </Grid>


          <Grid size={6}>
            <Typography variant="bodyText1" sx={{ color: "dark.main", mb: 0.5 }}>
              Cost to Customer (₹)
            </Typography>
            <TextField
              disabled={initialData}
              fullWidth
              placeholder="Enter customer cost"
              name="cost_to_customer"
              type="number"
              value={formData.cost_to_customer}
              onChange={handleChange} // Add onChange handler
              sx={inputBoxStyle}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
              }}
            />
          </Grid>

          <Grid size={6}>
            <Typography variant="bodyText1" sx={{ color: "dark.main", mb: 0.5 }}>
              Description
            </Typography>
            <TextField
              disabled={initialData}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              sx={inputBoxStyle}
            />
          </Grid>
        </Grid>
        <Grid size={12}>

          {/* ===== Upload / Replace button (ADD + EDIT both) ===== */}
          <Typography
            variant="body2"
            color="secondary"
            fontWeight={500}
            sx={{ cursor: "pointer" }}
            onClick={() => fileInputRef.current.click()}
          >
            <AttachmentIcon sx={{ verticalAlign: "middle", mr: 1 }} />

            {initialData
              ? "Add / Replace Image or Document"
              : "Add Image or Document"}
          </Typography>


          {/* ===== Existing file (EDIT mode) ===== */}
          {formData.attachment && !selectedFile && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                icon={<AttachmentIcon />}
                label={formData.attachment.split("/").pop()}
                onClick={() =>
                  window.open(`${FRAPPE_DIRECT_URL}${formData.attachment}`)
                }
                onDelete={() =>
                  setFormData(prev => ({ ...prev, attachment: null }))
                }
              />
            </Stack>
          )}


          {/* ===== Newly selected file ===== */}
          {selectedFile && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              {selectedFile && (
                <Chip
                  icon={<AttachmentIcon />}
                  label={selectedFile.name}
                  size="small"
                  onDelete={() => setSelectedFile(null)}
                />
              )}
            </Stack>
          )}

        </Grid>



      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{
          minWidth: 100,
          borderRadius: theme.borderRadius.medium,
          height: 36,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          // disabled={initialData}
          sx={{
            minWidth: 100,
            borderRadius: theme.borderRadius.medium,
            height: 36,

            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {initialData ? "Update" : "Save"}

        </Button>
      </DialogActions>

    </Dialog>

  );
}

export default function AvailedServices({
  showAddButton = true,
  showRowActions = false,
  showDateField = true,
  services = [],
  details = {},
  refresh = () => { }
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const { showLoader, hideLoader, loading } = useLoading();
  const { showNotification } = useNotification();

const handleAddServiceFromDialog = async (srvc, selectedFile, existingAttachment) => {
  try {
    showLoader();

    if (!details?.name) throw new Error("Reservation name missing");

    let fileUrl = existingAttachment || null;

    // upload file if exists
    if (selectedFile) {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("is_private", 0);
      fd.append("folder", "Home");

      const uploadRes = await uploadFrappeMedia(fd);
      fileUrl = uploadRes?.data?.message?.file_url;
    }

    const isEdit = !!srvc.name;   // ⭐ THIS LINE IS IMPORTANT

    const body = {
      name: details.name,
      service: {
        name: srvc.name, // update clue
        service_name: srvc.service,
        amount: srvc.cost_to_customer,
        expense: srvc.cost_to_company,
        description: srvc.description,
        invoice_amount: 0,
        markup_percentage: srvc.markup_percentage,
        attachment: fileUrl
      }
    };

    await consumeService(body);

    showNotification(
      "success",
      "Success",
      isEdit
        ? "Service updated successfully"
        : "Service added successfully"
    );

    setDialogOpen(false);
    setEditingIndex(null);
    refresh();
  }
  catch (err) {
    console.error(err);
    showNotification("error", "Failed", "Failed to save service");
  }
  finally {
    hideLoader();
  }
};


  //   const handleAddServiceFromDialog = async (srvc, selectedFile, existingAttachment) => {
  //   try {
  //     showLoader();

  //     if (!details?.name) {
  //       throw new Error("Reservation name missing");
  //     }

  //     let fileUrl = existingAttachment || null;

  //     /* ===============================
  //        STEP 1 — Upload file if exists
  //     =============================== */
  //     if (selectedFile) {
  //       const fd = new FormData();
  //       fd.append("file", selectedFile);
  //       fd.append("is_private", 0);
  //       fd.append("folder", "Home");

  //       const uploadRes = await uploadFrappeMedia(fd);
  //       fileUrl = uploadRes?.data?.message?.file_url;
  //     }

  //     /* ===============================
  //        STEP 2 — EDIT MODE (UPDATE)
  //        If row has name → update
  //     =============================== */
  //     if (srvc.name) {
  //       await saveDocs({
  //         doc: JSON.stringify({
  //           doctype: "Availed Services",
  //           name: srvc.name,   // ⭐ tells frappe which row to update

  //           service_name: srvc.service,
  //           amount: srvc.cost_to_customer,
  //           expense: srvc.cost_to_company,
  //           description: srvc.description,
  //           invoice_amount: 0,
  //           markup_percentage: srvc.markup_percentage,
  //           attachment: fileUrl
  //         }),
  //         action: "Save"
  //       });

  //       showNotification("success", "Updated", "Service updated successfully");
  //     }

  //     /* ===============================
  //        STEP 3 — ADD MODE (CREATE)
  //        No name → create new
  //     =============================== */
  //     else {
  //       const body = {
  //         name: details.name,
  //         service: {
  //           service_name: srvc.service,
  //           amount: srvc.cost_to_customer,
  //           expense: srvc.cost_to_company,
  //           description: srvc.description,
  //           invoice_amount: 0,
  //           markup_percentage: srvc.markup_percentage,
  //           attachment: fileUrl
  //         }
  //       };

  //       await consumeService(body);

  //       showNotification("success", "Success", "Service added successfully");
  //     }

  //     setDialogOpen(false);
  //     setEditingIndex(null);
  //     refresh();
  //   }
  //   catch (err) {
  //     console.error(err);
  //     showNotification("error", "Failed", "Failed to save service");
  //   }
  //   finally {
  //     hideLoader();
  //   }
  // };


  const handleRowClick = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };
  // const handleAddServiceFromDialog = async (srvc, selectedFiles) => {
  //     try {
  //       showLoader();

  //       console.log("Details passed to AvailedServices:", details);
  //       console.log("Service data to submit:", srvc);

  //       if (!details || !details.name) {
  //         throw new Error("Reservation name is required. Please ensure you're adding services from a valid reservation.");
  //       }

  //       let body = {
  //         name: details.name,
  //         service: {
  //           "service_name": srvc.service,
  //           "amount": srvc.cost_to_customer,
  //           "expense": srvc.cost_to_company,
  //           "description": srvc.description,
  //           "invoice_amount": 0,
  //           "markup_percentage": srvc.markup_percentage
  //         }
  //       }

  //       console.log("API Request Body:", body);

  //       // Step 1: Add service first
  //       let asc = await consumeService(body);
  //       console.log("API Response:", asc);

  //       // Step 2: If files are selected, upload and attach them
  //       if (selectedFiles?.length) {
  //         const childRowName = asc?.data?.message?.name;
  //         console.log("Child row name (Availed Service):", childRowName);

  //         if (!childRowName) {
  //           throw new Error("Failed to get availed service name for attachment");
  //         }

  //         // Upload and attach all files
  //         for (const file of selectedFiles) {
  //           try {
  //             // Step 2a: Upload the file first
  //             const formData = new FormData();
  //             formData.append("file", file);
  //             formData.append("doctype", "Availed Services");
  //             formData.append("docname", childRowName);

  //             const uploadRes = await uploadFrappeMedia(formData);
  //             console.log("Upload response:", uploadRes);

  //             const fileDoc = uploadRes?.data?.message;
  //             if (!fileDoc || !fileDoc.name) {
  //               throw new Error(`Failed to upload file: ${file.name}`);
  //             }

  //             console.log("File uploaded successfully:", fileDoc.name);

  //             // Step 2b: Use savedocs to properly link the file
  //             const attachPayload = {
  //               doc: JSON.stringify({
  //                 doctype: "File",
  //                 name: fileDoc.name,
  //                 file_name: fileDoc.file_name,
  //                 file_url: fileDoc.file_url,
  //                 attached_to_doctype: "Availed Services",
  //                 attached_to_name: childRowName,
  //                 attached_to_field: null,
  //                 folder: fileDoc.folder || "Home",
  //                 is_private: fileDoc.is_private || 0
  //               }),
  //               action: "Save"
  //             };

  //             console.log("Attach payload:", attachPayload);

  //             const attachRes = await saveDocs(attachPayload);
  //             console.log("File attached successfully:", attachRes);

  //           } catch (fileError) {
  //             console.error(`Error processing file ${file.name}:`, fileError);
  //             // Continue with other files even if one fails
  //             showNotification('warning', 'Warning', `Failed to attach file: ${file.name}`);
  //           }
  //         }
  //       }

  //       showNotification('success', 'Success', 'Service added successfully with attachments');
  //       setDialogOpen(false);
  //       setEditingIndex(null);
  //       refresh();
  //     }
  //     catch (err) {
  //       console.error("Error adding service:", err);
  //       let errorMessage = 'Failed to add service.';

  //       if (err.message && err.message.includes("Reservation name is required")) {
  //         errorMessage = 'Reservation name is required. Please ensure you\'re adding services from a valid reservation.';
  //       } else if (err.response && err.response.data) {
  //         errorMessage = err.response.data.message || errorMessage;
  //       }

  //       showNotification('error', 'Failed', errorMessage);
  //     } finally {
  //       hideLoader();
  //     }
  //   };
  const handleAddClick = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  useEffect(() => {
    console.log("AvailedServices - details prop:", details);
  }, [details]);

  return (
    <Paper elevation={3} sx={{
      p: 2.5,
      borderRadius: 3,
      backgroundColor: theme.palette.light.main,
      position: "relative",
      overflow: "auto",
      height: 300,
      maxHeight: "500px",
    }}>
      <Typography variant="SubTitleBold">Availed Services</Typography>

      {details && details.name && (
        <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
          Reservation: {details.name}
        </Typography>
      )}

      {showAddButton && (
        <IconButton
          onClick={handleAddClick}
          disabled={!details || !details.name}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            backgroundColor: "light.main",
            boxShadow: 2,
            border: "2px solid",
            borderColor: (!details || !details.name) ? "gray.main" : "primary.main",
            borderRadius: "50%",
            width: 40,
            height: 40,
            p: 1,
            ...((!details || !details.name) && {
              cursor: 'not-allowed',
              opacity: 0.6
            })
          }}
        >
          <AddIcon sx={{ color: (!details || !details.name) ? "gray.main" : "primary.main" }} />
        </IconButton>
      )}
      {/* Only show the error message when showAddButton is true */}
      {showAddButton && (!details || !details.name) && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          Cannot add services: No reservation selected
        </Typography>
      )}

      {/* Table Header */}
      <Box display="flex" bgcolor="secondary.main" borderRadius={1} px={4} py={1} mt={2}>
        {showDateField && <Box width="15%"><Typography fontWeight={700} fontSize={16} fontFamily='Nunito Sans'>Date</Typography></Box>}
        <Box width={showDateField ? "25%" : "30%"}><Typography fontWeight={700} fontSize={16} fontFamily='Nunito Sans'>Service</Typography></Box>
        <Box width={showDateField ? "35%" : "45%"}><Typography fontWeight={700} fontSize={16} fontFamily='Nunito Sans' ml={1}>Description</Typography></Box>
        <Box width="20%"><Typography fontWeight={700} fontSize={16} ml={4} fontFamily='Nunito Sans'>Cost(₹)</Typography></Box>
        {showRowActions && <Box width="5%" />}
      </Box>
      {
        (services.length == 0) && <Box
          display="flex"
          alignItems="center"
          justifyContent={"center"}
          px={0.5}
          py={0.5}
          gap={0.5}
        >
          <Typography sx={{
            border: "1px solid #CFCFCF",
            borderRadius: 2,
            px: 1,
            py: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            No Services availed
          </Typography>
        </Box>
      }

      {/* Table Body */}
      {services.map((item, index) => (
        <Box
          key={index}
          display="flex"
          alignItems="center"
          px={0.5}
          py={0.5}
          gap={0.5}
          onClick={() => handleRowClick(index)}
          sx={{
            cursor: 'pointer',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          {showDateField && (
            <Box sx={{ width: "15%", flexShrink: 0 }}>
              <Typography sx={{
                border: "1px solid #CFCFCF",
                borderRadius: 2,
                px: 1,
                py: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {moment(item.creation).format("DD-MM-YYYY")}
              </Typography>
            </Box>
          )}
          <Box sx={{ width: showDateField ? "25%" : "30%", flexShrink: 0 }}>
            <Typography sx={{
              border: "1px solid #CFCFCF",
              borderRadius: 2,
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {item.service_name}
            </Typography>
          </Box>
          <Box sx={{ width: showDateField ? "35%" : "45%", flexShrink: 0 }}>
            <Typography sx={{
              border: "1px solid #CFCFCF",
              borderRadius: 2,
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {item.description}
            </Typography>
          </Box>
          <Box sx={{ width: "23%", flexShrink: 0 }}>
            <Typography sx={{
              border: "1px solid #CFCFCF",
              borderRadius: 2,
              px: 1,
              py: 0.5,
              textAlign: "right",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              ₹ {item.amount.toLocaleString()}
            </Typography>
          </Box>
          {showRowActions && (
            <Box
              sx={{
                width: "3%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "green.main",
                borderRadius: "50%",
                height: 20,
                flexShrink: 0
              }}
            >
              <IconButton size="small" sx={{ color: "white", p: 0.5 }}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}

      {/* Dialog */}
      <AddServiceDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingIndex(null);
        }}
        onAdd={handleAddServiceFromDialog}
        loading={loading}
        showDateField={showDateField}
        initialData={editingIndex !== null ? {
          name: services[editingIndex].name,
          date: moment(services[editingIndex].creation).format("YYYY-MM-DD"),
          service: services[editingIndex].service_name,
          description: services[editingIndex].description,
          cost_to_company: services[editingIndex].expense || "",
          markup_percentage: services[editingIndex].markup_percentage || "",
          cost_to_customer: services[editingIndex].amount || "",
          attachment: services[editingIndex].attachment || null
        } : null}
      />
    </Paper>
  );
}